import { Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon } from '@persona-id/design-tokens/icons';
import Persona from 'persona';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { SUPPORTED_PLANS_FOR_SELF_SERVE_UPGRADE, View } from 'dashboard/components/PlanUpgradeFlow';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Organization } from 'dashboard/models/organization';
import { AvailableOrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import { User } from 'dashboard/models/user';
import { RootState } from 'dashboard/redux/store';

interface BizVerificationInquiryProps {
  newOrganizationPlan: AvailableOrganizationPlan;
  organization: Organization;
  currentUser: User;
  requestedFeatureName?: string;
  handleCreateOrganizationPlanMigration: (
    inquiryId: string,
    selectedPlanKey: string,
  ) => Promise<OrganizationPlanMigration>;
  handleNavToNextView: (
    currentView: View,
    newOrganizationPlan: AvailableOrganizationPlan | null,
  ) => void;
  handleNavToPreviousView: (currentView: View) => void;
  onClose: () => void;
}

const INQUIRY_TEMPLATE_TOKEN = 'itmpl_Dw7eVNEyZfBsDkBabtfnMeQj';
// We always create Inquiries in the production environment (as opposed to Sandbox) of the Persona Business Verification
// organization because we have a different Persona Business Verification org available in local, staging, and production.
const PERSONA_BUSINESS_VERIFICATION_ORGANIZATION_INQUIRY_ENVIRONMENT_TYPE = 'production';
const INQUIRY_HOST = (() => {
  if (process.env.NODE_ENV === 'production') {
    if (window.location.hostname.includes('withpersona-staging')) {
      return 'staging';
    } else {
      return 'production';
    }
  } else {
    return 'development';
  }
})();

const BizVerificationInquiry = ({
  newOrganizationPlan,
  organization,
  currentUser,
  requestedFeatureName,
  handleCreateOrganizationPlanMigration,
  handleNavToNextView,
  handleNavToPreviousView,
  onClose,
}: BizVerificationInquiryProps) => {
  const { currentOrganizationPlan } = useSelector((state: RootState) => ({
    currentOrganizationPlan: state.organization.currentOrganizationPlan,
  }));

  const [isInquiryLoaded, setIsInquiryLoaded] = useState(false);

  const newOrganizationPlanKey = newOrganizationPlan.key;
  const usePlanMigration = SUPPORTED_PLANS_FOR_SELF_SERVE_UPGRADE.includes(newOrganizationPlanKey);

  return (
    <StyledModalContainer>
      <ModalHeader
        prefixIcon={<ArrowLeftIcon size={20} />}
        onPrefixIconClick={() => handleNavToPreviousView(View.BizVerificationInquiry)}
        onClose={onClose}
      >
        Verify your information
      </ModalHeader>
      <ModalBody>
        <InquiryBody>
          {!isInquiryLoaded && <LoadingIndicator />}
          <Persona.Inquiry
            host={INQUIRY_HOST}
            templateId={INQUIRY_TEMPLATE_TOKEN}
            environment={PERSONA_BUSINESS_VERIFICATION_ORGANIZATION_INQUIRY_ENVIRONMENT_TYPE}
            onLoad={() => {
              setIsInquiryLoaded(true);
            }}
            onError={() => {
              setIsInquiryLoaded(true);
            }}
            onComplete={({ inquiryId }) => {
              if (SUPPORTED_PLANS_FOR_SELF_SERVE_UPGRADE.includes(newOrganizationPlanKey)) {
                handleCreateOrganizationPlanMigration(inquiryId, newOrganizationPlanKey)
                  .then(() => {
                    handleNavToNextView(View.BizVerificationInquiry, newOrganizationPlan);
                  })
                  .catch((error) => {
                    console.error('Error creating plan migration:', error);
                  });
              } else {
                handleNavToNextView(View.BizVerificationInquiry, newOrganizationPlan);
              }
            }}
            frameHeight='600px'
            referenceId={currentUser.id}
            fields={{
              persona_user_id: currentUser.id,
              persona_organization_id: organization.id,
              email_address: currentUser.attributes['email-address'],
              name_business: organization.attributes.name,
              selected_plan: newOrganizationPlanKey,
              existing_plan: currentOrganizationPlan?.attributes?.key,
              // Note: This is a temporary field passed in to help Persona DD Org understand if it
              // should wait for a TXN to kick off he review process
              organization_plan_flipper: usePlanMigration,
              ...(requestedFeatureName && { requested_product: requestedFeatureName }),
            }}
          />
        </InquiryBody>
      </ModalBody>
    </StyledModalContainer>
  );
};

const StyledModalContainer = styled(ModalContainer)`
  min-width: 700px;
`;

const InquiryBody = styled.div`
  height: 608px;
  width: 520px;
  display: block;
  margin: auto;
  margin-top: ${Spacing[4]};

  & > iframe {
    width: 100%;
    height: 100%;
  }
`;

export default BizVerificationInquiry;
